import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion, type PanInfo } from 'framer-motion'
import { Button } from '@structural-climbing/ui'

interface Props {
  /** The image to show, or null when the viewer is closed. */
  src: string | null
  alt?: string
  onClose: () => void
}

/** How far, or how fast, a drag counts as "put it away". */
export function shouldDismissImage(offsetY: number, velocityY: number): boolean {
  return Math.abs(offsetY) > 120 || Math.abs(velocityY) > 700
}

/**
 * An image on its own, filling the screen: a news photo, a route's photo.
 *
 * Closes the ways people expect from a phone's photo viewer: the ✕, a tap on
 * the dark around the picture, a flick up or down, and Escape on a keyboard.
 * Pinch-zoom is left to the browser, so a screenshot in a news post can be
 * read up close. Portalled to the body so no sticky header or sheet can trap
 * it.
 */
export function ImageViewer({ src, alt = '', onClose }: Props) {
  const { t } = useTranslation()

  useEffect(() => {
    if (!src) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [src, onClose])

  const onDragEnd = (_: unknown, info: PanInfo) => {
    if (shouldDismissImage(info.offset.y, info.velocity.y)) onClose()
  }

  return createPortal(
    <AnimatePresence>
      {src && (
        <motion.div
          key="image-viewer"
          role="dialog"
          aria-modal="true"
          aria-label={alt || t('common.image')}
          data-testid="image-viewer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[var(--z-media)] flex items-center justify-center bg-black/95 px-[max(0.5rem,var(--safe-l,0px))] pb-[var(--safe-b)] pt-[var(--safe-t)]"
          onClick={onClose}
        >
          <motion.img
            src={src}
            alt={alt}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={0.7}
            onDragEnd={onDragEnd}
            initial={{ scale: 0.96 }}
            animate={{ scale: 1 }}
            // A tap on the picture itself leaves it open; only the dark closes.
            onClick={(e) => e.stopPropagation()}
            className="max-h-full max-w-full select-none object-contain"
            style={{ touchAction: 'pinch-zoom' }}
            draggable={false}
          />
          <Button
            isIconOnly
            radius="full"
            aria-label={t('common.close')}
            onPress={onClose}
            className="absolute right-4 top-[calc(var(--safe-t)+1rem)] bg-white/15 text-lg text-white backdrop-blur"
          >
            ✕
          </Button>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
